import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { X } from 'react-feather';
import { ServerPath } from '../../Constant';
import { _axios } from '../../utils/axios';

const inputStyle = {
    height: '46px',
    border: '1px solid #E2E8F0',
    backgroundColor: '#FFFFFF',
    borderRadius: '6px',
};

const labelStyle = {
    color: '#4A5568',
    fontWeight: '500',
    fontSize: '16px',
};

const streamTypeBadge = (type) => {
    const config = {
        youtube: { label: 'YouTube', bg: '#FF0000' },
        cctv: { label: 'Internal', bg: '#00BCD4' },
        cctv_external: { label: 'External', bg: '#FF9800' },
    }[type] || { label: type, bg: '#9E9E9E' };
    return (
        <span style={{
            display: 'inline-block',
            padding: '2px 8px',
            borderRadius: '12px',
            backgroundColor: config.bg,
            color: '#fff',
            fontSize: '10px',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '0.3px',
        }}>
            {config.label}
        </span>
    );
};

const StreamPickerOption = ({ stream, isSelected, onToggle }) => (
    <div
        onClick={() => onToggle(stream)}
        style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '10px 12px',
            borderRadius: '8px',
            border: `1px solid ${isSelected ? '#5662F6' : '#E2E8F0'}`,
            background: isSelected ? '#F0F1FF' : '#FFFFFF',
            cursor: 'pointer',
            transition: 'all 0.15s ease',
        }}
    >
        <Form.Check
            type="checkbox"
            checked={isSelected}
            onChange={() => onToggle(stream)}
            onClick={(e) => e.stopPropagation()}
            style={{ margin: 0 }}
        />
        {stream.thumbnailUrl ? (
            <img
                src={stream.thumbnailUrl}
                alt={stream.streamTitle}
                style={{ width: '56px', height: '40px', objectFit: 'cover', borderRadius: '4px', flexShrink: 0 }}
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
        ) : (
            <div style={{
                width: '56px', height: '40px', borderRadius: '4px',
                background: '#F1F5F9', flexShrink: 0,
            }} />
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
                fontSize: '14px',
                fontWeight: '500',
                color: '#1F2937',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
            }}>
                {stream.streamTitle}
            </div>
            <div style={{ marginTop: '4px' }}>{streamTypeBadge(stream.streamType)}</div>
        </div>
    </div>
);

const SelectedChip = ({ stream, onRemove }) => (
    <span style={{
        backgroundColor: '#5662F6',
        color: 'white',
        padding: '6px 10px',
        borderRadius: '6px',
        fontSize: '13px',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        maxWidth: '100%',
    }}>
        <span style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '200px',
        }}>
            {stream.streamTitle}
        </span>
        <button
            type="button"
            onClick={() => onRemove(stream.id)}
            className="border-0 bg-transparent text-white p-0"
            style={{ lineHeight: 1, cursor: 'pointer', display: 'flex', alignItems: 'center' }}
            aria-label={`Remove ${stream.streamTitle}`}
        >
            <X size={14} />
        </button>
    </span>
);

const CreateMultiView = ({ show, onHide }) => {
    const [title, setTitle] = useState('');
    const [eligibleStreams, setEligibleStreams] = useState([]);
    const [selectedStreams, setSelectedStreams] = useState([]);
    const [searchValue, setSearchValue] = useState('');
    const [loadingStreams, setLoadingStreams] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        fetchEligibleStreams();
    }, []);

    const fetchEligibleStreams = async () => {
        try {
            setLoadingStreams(true);
            const response = await _axios.get(`${ServerPath}/live-darshan/multi-view/eligible-streams`);
            if (response.data.success === 1) {
                setEligibleStreams(response.data.data || []);
            } else {
                toast.error(response.data.message || 'Failed to fetch eligible streams');
            }
        } catch (error) {
            console.error('Error fetching eligible streams:', error);
            toast.error(error.response?.data?.message || 'Error fetching eligible streams');
        } finally {
            setLoadingStreams(false);
        }
    };

    const handleToggleStream = (stream) => {
        setSelectedStreams(prev => {
            const exists = prev.some(s => s.id === stream.id);
            if (exists) return prev.filter(s => s.id !== stream.id);
            return [...prev, stream];
        });
    };

    const handleRemoveSelected = (id) => {
        setSelectedStreams(prev => prev.filter(s => s.id !== id));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isSubmitting) return;

        const trimmedTitle = title.trim();
        if (!trimmedTitle) {
            toast.error('Title is required');
            return;
        }
        if (trimmedTitle.length > 255) {
            toast.error('Title cannot exceed 255 characters');
            return;
        }
        if (selectedStreams.length < 2) {
            toast.error('At least 2 streams are required');
            return;
        }

        try {
            setIsSubmitting(true);
            const payload = {
                title: trimmedTitle,
                stream_ids: selectedStreams.map(s => s.id),
            };
            const response = await _axios.post(`${ServerPath}/live-darshan/multi-view/create`, payload);
            if (response.data.success === 1) {
                toast.success('Multi View group created');
                setTimeout(() => onHide(), 800);
            } else {
                toast.error(response.data.message || 'Failed to create Multi View group');
                setIsSubmitting(false);
            }
        } catch (error) {
            console.error('Error creating Multi View group:', error);
            const status = error.response?.status;
            const msg = error.response?.data?.message;
            if (status === 400 && msg) {
                toast.error(msg);
            } else if (status >= 500) {
                toast.error('Something went wrong');
            } else {
                toast.error(msg || 'Error creating Multi View group');
            }
            setIsSubmitting(false);
        }
    };

    const filteredStreams = eligibleStreams.filter(s =>
        (s.streamTitle || '').toLowerCase().includes(searchValue.toLowerCase())
    );

    return (
        <Modal show={show} onHide={onHide} size="lg" centered style={{ borderRadius: '10px' }}>
            <div style={{ borderRadius: '10px' }}>
                <Modal.Header closeButton style={{ borderBottom: '1px solid #E2E8F0', padding: '20px 24px' }}>
                    <Modal.Title style={{ fontWeight: '600', fontSize: '22px' }}>Create Multi View</Modal.Title>
                </Modal.Header>

                <Modal.Body style={{ padding: '24px' }}>
                    <Form>
                        <Row className="mb-4">
                            <Col md={12}>
                                <Form.Group>
                                    <Form.Label className="mb-2" style={labelStyle}>
                                        Multi View Title <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        placeholder="e.g., Pandharpur Vitthal — All Cameras"
                                        maxLength={255}
                                        style={inputStyle}
                                    />
                                    <Form.Text className="text-muted">
                                        Internal label — admin-only. Max 255 characters.
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="mb-2" style={labelStyle}>
                                        Select Multi View Live Streams <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={searchValue}
                                        onChange={(e) => setSearchValue(e.target.value)}
                                        placeholder="Search eligible live streams..."
                                        style={{ ...inputStyle, marginBottom: '8px' }}
                                    />
                                    <div style={{
                                        border: '1px solid #E2E8F0',
                                        borderRadius: '6px',
                                        padding: '8px',
                                        height: '320px',
                                        overflowY: 'auto',
                                        background: '#FAFAFA',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '6px',
                                    }}>
                                        {loadingStreams && (
                                            <div style={{ textAlign: 'center', color: '#A0AEC0', padding: '20px', fontSize: '13px' }}>
                                                Loading...
                                            </div>
                                        )}
                                        {!loadingStreams && filteredStreams.length === 0 && (
                                            <div style={{ textAlign: 'center', color: '#A0AEC0', padding: '20px', fontSize: '13px' }}>
                                                No eligible streams. A stream must be Live Now with Multi View enabled and not already in another group.
                                            </div>
                                        )}
                                        {!loadingStreams && filteredStreams.map(stream => (
                                            <StreamPickerOption
                                                key={stream.id}
                                                stream={stream}
                                                isSelected={selectedStreams.some(s => s.id === stream.id)}
                                                onToggle={handleToggleStream}
                                            />
                                        ))}
                                    </div>
                                    <Form.Text className="text-muted">
                                        Pick at least 2 streams. Streams appear in the order they were added (newest first).
                                    </Form.Text>
                                </Form.Group>
                            </Col>

                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="mb-2" style={labelStyle}>
                                        Selected ({selectedStreams.length})
                                    </Form.Label>
                                    <div style={{
                                        border: '1px solid #E2E8F0',
                                        borderRadius: '6px',
                                        padding: '12px',
                                        minHeight: '370px',
                                        maxHeight: '370px',
                                        overflowY: 'auto',
                                        background: '#FAFAFA',
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: '8px',
                                        alignContent: 'flex-start',
                                    }}>
                                        {selectedStreams.length === 0 && (
                                            <span style={{ color: '#A0AEC0', fontSize: '13px' }}>
                                                No streams selected
                                            </span>
                                        )}
                                        {selectedStreams.map(stream => (
                                            <SelectedChip
                                                key={stream.id}
                                                stream={stream}
                                                onRemove={handleRemoveSelected}
                                            />
                                        ))}
                                    </div>
                                </Form.Group>
                            </Col>
                        </Row>
                    </Form>
                </Modal.Body>

                <div style={{ padding: '0 24px 24px 0', display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        variant="secondary"
                        onClick={onHide}
                        className="me-2"
                        style={{ borderRadius: '6px', padding: '10px 24px', fontWeight: '500', fontSize: '16px' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        style={{
                            backgroundColor: '#5662F6',
                            borderColor: '#5662F6',
                            borderRadius: '6px',
                            padding: '10px 40px',
                            fontWeight: '500',
                            fontSize: '16px',
                        }}
                    >
                        {isSubmitting ? 'Saving...' : 'Save'}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default CreateMultiView;
